/**
 * How the native shell hands its session to the pages it embeds.
 *
 * THE PROBLEM. The app signs in natively and keeps the session in the iPhone
 * keychain. A WKWebView has its own cookie jar and knows nothing about that,
 * so the tabs that are still web pages — Obsah, Profil — render the login
 * screen to someone who is already signed in to the app.
 *
 * WHY NOT THE URL. The web already has a door that takes a session:
 * `/auth/callback#access_token=…&refresh_token=…`. It works, and it is the
 * wrong door for this. Supabase's implicit-flow fragment is sticky across
 * client-side navigation, and `window.location.href` is what analytics
 * reports — which is exactly how 2,016 people's refresh tokens ended up in
 * PostHog (fixed separately). Routing the app's tokens through the same
 * mechanism would have added to that pile.
 *
 * WHY NOT A COOKIE. The shell could write `supabase-auth-token` itself: the
 * cookie is not httpOnly and its format is known. That is forging another
 * system's internal format from outside it — the same mistake as hiding the
 * web's tab bar with injected CSS, which broke the moment the markup did not
 * match the guess.
 *
 * WHAT THIS IS. The shell sets one global before the page's own scripts run;
 * the page reads it once, adopts the session through supabase-js, and clears
 * it. The tokens never enter a URL, never reach a server as a query, and are
 * never reported by analytics.
 *
 *    native (keychain)
 *          │  injectedJavaScriptBeforeContentLoaded
 *          ▼
 *    window.__ksebe_app_session = {access_token, refresh_token}
 *          │  read once, then deleted
 *          ▼
 *    supabase.auth.setSession()  ──►  ordinary web session, cookie and all
 *
 * NOT A PRIVILEGE ESCALATION. Anything that can set this global on our origin
 * can already run script on our origin. And the value is only useful if it is
 * a *valid* pair of tokens, which the setter would have to have obtained
 * legitimately. Supabase verifies them; a forged pair fails there.
 */

export const APP_SESSION_GLOBAL = '__ksebe_app_session'

export interface HandedSession {
  access_token: string
  refresh_token: string
}

const looks_like_token = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

/**
 * Read the handed session and REMOVE it, so it is adopted at most once.
 *
 * Single-use matters: leaving it in place would re-run `setSession` on every
 * client-side navigation, each one firing a fresh SIGNED_IN and the
 * post-sign-in hooks behind it.
 */
export const take_handed_session = (
  win: {[key: string]: unknown} | undefined,
): HandedSession | null => {
  if (!win) return null
  const raw = win[APP_SESSION_GLOBAL]
  if (raw === undefined) return null

  // Delete BEFORE validating, not after. A malformed value is still a value:
  // if the shell ever handed over a serialized pair instead of an object, the
  // old order rejected it and left it sitting on `window` for every later
  // script on the page to read. Take it out of reach first, then decide
  // whether it was usable.
  delete win[APP_SESSION_GLOBAL]

  if (!raw || typeof raw !== 'object') return null

  const {access_token, refresh_token} = raw as Record<string, unknown>
  if (!looks_like_token(access_token) || !looks_like_token(refresh_token)) {
    return null
  }
  return {access_token, refresh_token}
}

/**
 * The `sub` claim of a JWT, without verifying it.
 *
 * Used ONLY to answer "is this the same person the page already has?" so an
 * identical session is not re-adopted. It is not an authorisation decision —
 * Supabase verifies the token when the session is actually set — so reading
 * the payload without checking the signature is safe here and avoids a network
 * round trip on every embedded page load.
 */
export const subject_of = (jwt: string): string | null => {
  try {
    const payload = jwt.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const sub = JSON.parse(json)?.sub
    return typeof sub === 'string' ? sub : null
  } catch {
    return null
  }
}

/**
 * Should the page adopt what the shell handed it?
 *
 * No when it is already that person — adopting again would churn the cookie
 * and fire the sign-in hooks for nothing. Yes when signed out, and yes when
 * the page holds a DIFFERENT person: that is a stale webview cookie from
 * whoever used the app before, and the app's own session is the current truth.
 */
export const should_adopt = (opts: {
  handed: HandedSession
  current_user_id: string | null
}): boolean => {
  if (!opts.current_user_id) return true
  return subject_of(opts.handed.access_token) !== opts.current_user_id
}
